// A lightweight IndexedDB-backed outbox for structured API mutations made while
// offline. The api client (lib/api.ts) enqueues a mutation when a write fails
// with a network error; queued writes replay automatically when connectivity
// returns via the foreground path (the 'online' event and window focus, which
// works in every browser) and, on Chromium, via the service worker's Background
// Sync `sync` handler.
//
// The service worker (public/sw.js) reads this SAME database + store to replay
// in the background when no app window is open. Keep DB_NAME, STORE_NAME, the
// record shape, OFFLINE_SYNC_TAG, and OFFLINE_FLUSH_MESSAGE in sync across both
// files.

import { expectedIdentityHeaders, OFFLINE_REPLAY_HEADER } from "./expectedIdentity.js";

const DB_NAME = "household-offline";
const DB_VERSION = 1;
const STORE_NAME = "mutations";

// Shared with public/sw.js — do not rename without updating both files.
export const OFFLINE_SYNC_TAG = "household-offline-sync";
export const OFFLINE_FLUSH_MESSAGE = "household-offline-flush";

export type QueuedMutation = {
  id: string;
  method: string;
  url: string;
  body: string | null;
  idempotencyKey: string;
  /**
   * The write id the original attempt sent (#800), replayed with it so the
   * realtime event this write eventually produces still names it. Without it a
   * replayed write's echo is unattributed, and an unattributed echo can be
   * swallowed by whatever mark happens to be live — the very failure this issue
   * is about, reached by going offline. Optional: a record written before this
   * shipped simply has none.
   */
  writeId?: string;
  /**
   * The household and person the write was made for (#105), replayed as the
   * request's expectation so the server can tell when an outbox entry lands in
   * a different household or under a different sign-in than it was made in.
   * Optional: entries queued before this shipped carry neither, and replay
   * exactly as they always did.
   */
  householdId?: string;
  userId?: string;
  createdAt: number;
};

type SyncCapableRegistration = ServiceWorkerRegistration & {
  sync?: { register(tag: string): Promise<void> };
};

/**
 * A fresh opaque id. Used both for the outbox record's own key and, via
 * `newRequestId`, for the request id a write carries from its first attempt
 * through every replay (#798).
 */
function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Fall through to the non-crypto id below.
  }
  return `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Mint the id that identifies one logical write to the server (#798).
 *
 * Exported so `lib/api.ts` can stamp it on the FIRST attempt. That timing is
 * the whole fix: an id minted when the write is queued is one the server never
 * saw on the attempt that may already have committed, so it cannot recognise
 * the replay as the same request and the write lands twice.
 */
export function newRequestId(): string {
  return newId();
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((error) => {
    // Let a later call retry the open (private-mode / transient failures).
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function putMutation(record: QueuedMutation): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, "readwrite");
  tx.objectStore(STORE_NAME).put(record);
  await txDone(tx);
}

async function deleteMutation(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_NAME, "readwrite");
  tx.objectStore(STORE_NAME).delete(id);
  await txDone(tx);
}

async function getAllMutations(): Promise<QueuedMutation[]> {
  const db = await openDb();
  const items = await new Promise<QueuedMutation[]>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve((request.result as QueuedMutation[]) ?? []);
    request.onerror = () => reject(request.error);
  });
  // Replay in the order the writes were made so dependent mutations stay ordered.
  return items.slice().sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * The queued mutations whose URL path matches, oldest first (#1018).
 *
 * A surface that added a row optimistically needs to know its write is still
 * queued: the row lives in React state, so a reload while still offline drops
 * it from view even though the write is safe in here. That is the failure mode
 * that invites the user to add the same thing twice. `countQueuedMutations`
 * could only say *how many* writes are outstanding, never which — so this
 * returns the records and lets the caller rebuild exactly its own pending rows.
 *
 * Matching is on the path only: a query string is not part of what identifies
 * the endpoint, and callers compare against a literal like
 * "/api/v1/grocery-catalog/activate".
 */
export async function queuedMutationsFor(pathPrefix: string): Promise<QueuedMutation[]> {
  try {
    const items = await getAllMutations();
    return items.filter((item) => (item.url.split("?")[0] ?? item.url).startsWith(pathPrefix));
  } catch {
    // Storage unavailable: the caller renders nothing extra, which is the same
    // as it did before this existed.
    return [];
  }
}

export async function countQueuedMutations(): Promise<number> {
  try {
    const db = await openDb();
    return await new Promise<number>((resolve, reject) => {
      const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return 0;
  }
}

type PendingListener = (count: number) => void;
const pendingListeners = new Set<PendingListener>();

// React subscribes to keep the "waiting to sync" indicator current. The listener
// is called immediately with the current count and on every subsequent change.
export function subscribePendingCount(listener: PendingListener): () => void {
  pendingListeners.add(listener);
  void countQueuedMutations().then(listener).catch(() => undefined);
  return () => {
    pendingListeners.delete(listener);
  };
}

function emitPendingCount(): void {
  void countQueuedMutations()
    .then((count) => {
      for (const listener of pendingListeners) listener(count);
    })
    .catch(() => undefined);
}

async function requestBackgroundSync(): Promise<void> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const registration = (await navigator.serviceWorker.ready) as SyncCapableRegistration;
    if (registration.sync) await registration.sync.register(OFFLINE_SYNC_TAG);
  } catch {
    // Background Sync is Chromium-only; the 'online'/focus foreground flush is
    // the primary path and covers Safari/Firefox.
  }
}

export async function enqueueMutation(input: {
  method: string;
  url: string;
  body: string | null;
  /** The write id the attempt that failed already sent (#800). */
  writeId?: string;
  /** The request id the first attempt already sent; see below. */
  idempotencyKey?: string;
  /** The household and person the first attempt expected (#105). */
  householdId?: string;
  userId?: string;
  /**
   * When that first attempt went out. Defaults to now for a caller that did not
   * go through `api()`, but `api()` passes the real one: the replay horizon has
   * to be measured from when the server could have created its ledger row, not
   * from when the failure was finally noticed (codex review).
   */
  attemptedAt?: number;
}): Promise<QueuedMutation> {
  const id = newId();
  const record: QueuedMutation = {
    id,
    method: input.method,
    url: input.url,
    body: input.body,
    ...(input.writeId ? { writeId: input.writeId } : {}),
    // The id the FIRST attempt sent, so the server recognises a replay as the
    // same request and answers it without writing again (#798). It has to come
    // from the caller: minting one here would name a request the server never
    // saw on the attempt that may well have committed — which is exactly the
    // ambiguous-failure case the outbox exists for. The record id is only a
    // fallback for a caller that did not go through `api()`.
    idempotencyKey: input.idempotencyKey ?? id,
    ...(input.householdId ? { householdId: input.householdId } : {}),
    ...(input.userId ? { userId: input.userId } : {}),
    // The first attempt's send time, not the enqueue time — see `attemptedAt`.
    // Also what orders the queue, which is unchanged by this: both are stamped
    // per write and monotonic within a session.
    createdAt: input.attemptedAt ?? Date.now()
  };
  await putMutation(record);
  emitPendingCount();
  void requestBackgroundSync();
  return record;
}

// 4xx are the server's final answer and can't be fixed by retrying, except
// auth/timeout/rate-limit which are worth a later attempt. Anything else that
// isn't ok (5xx) is treated as transient.
function isPermanentRejection(status: number): boolean {
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * How old a queued write may be and still be replayed (#798, codex review).
 *
 * Paired with `LEDGER_RETENTION_DAYS` in `apps/api/src/services/idempotency.ts`
 * and deliberately equal to it. The server's idempotency ledger is what makes a
 * replay safe — it is how the server recognises an attempt that may already have
 * committed. Once the ledger row has been pruned, replaying is no longer a
 * replay: it is a fresh write, and if the original DID commit, that is the
 * duplicate this whole change exists to prevent.
 *
 * So past this age the entry is dropped instead. A write that has been waiting
 * three months is one nobody is still expecting, and a silently duplicated cart
 * is worse than a stale one that never lands. Neither outbox otherwise bounds
 * how long an entry can sit.
 */
/**
 * How long a queued write stays replayable: 83 days, well inside the server's
 * `LEDGER_RETENTION_DAYS` (apps/api/src/services/idempotency.ts, currently 180).
 *
 * Matching the two exactly is not safe (codex review). The client has to give
 * up strictly before the server forgets, and four things push the other way:
 *
 * - the ledger row is written when the first attempt *reaches* the server,
 *   which is always earlier than the moment that attempt is seen to fail and
 *   the entry is stamped — so the row is already older than the entry that
 *   would replay it;
 * - the sweep runs daily, so a row can go up to a day after it becomes
 *   eligible;
 * - a device that has been offline for months is exactly the device whose
 *   clock has drifted, and it is the client's clock that ages the entry;
 * - worse, that clock can move BACKWARDS — a phone resyncing after months
 *   offline — which no margin computed on it can detect (codex review).
 *
 * The first three need days; the last one is why the server's side is 180
 * rather than 90. The margin is the budget for how wrong this clock may be, so
 * it is bought where it is cheap: on the server, whose clock is the honest one.
 * It is a bound, not a proof — see `LEDGER_RETENTION_DAYS` for the residual.
 *
 * Dropping an entry costs nothing real either way: one that has sat unsent for
 * 83 days is not a write anyone is still waiting on.
 * `outboxReplayAge.test.ts` holds the margin.
 */
export const OUTBOX_MAX_REPLAY_AGE_DAYS = 83;
const OUTBOX_MAX_REPLAY_AGE_MS = OUTBOX_MAX_REPLAY_AGE_DAYS * 24 * 60 * 60 * 1000;

/**
 * Past this age a queued write must be dropped rather than replayed (#798).
 *
 * The server's idempotency ledger is garbage-collected on the same bound, so an
 * older entry would be sent with a request id the server no longer recognises
 * and treated as a brand-new write — duplicating exactly what the id exists to
 * prevent. Exported because there is more than one outbox: the shopping surface
 * keeps its own queue in localStorage and has to expire on the same horizon
 * (codex review).
 */
export function isBeyondReplayHorizon(createdAt: number, now: number): boolean {
  return now - createdAt > OUTBOX_MAX_REPLAY_AGE_MS;
}

function isTooOldToReplay(item: QueuedMutation, now: number): boolean {
  return isBeyondReplayHorizon(item.createdAt, now);
}

let flushing = false;

// Replay queued mutations oldest-first. Stops (preserving order) at the first
// transient failure so nothing after it jumps the queue. Returns how many
// applied and how many remain.
export async function flushQueue(): Promise<{ sent: number; remaining: number }> {
  if (flushing) return { sent: 0, remaining: await countQueuedMutations() };
  flushing = true;
  let sent = 0;
  try {
    const items = await getAllMutations();
    const now = Date.now();
    for (const item of items) {
      if (isTooOldToReplay(item, now)) {
        // Older than the server's idempotency ledger keeps its answer, so a
        // replay could no longer be recognised as one (#798, codex review).
        await deleteMutation(item.id);
        continue;
      }
      let response: Response;
      try {
        response = await fetch(item.url, {
          method: item.method,
          headers: {
            "content-type": "application/json",
            "Idempotency-Key": item.idempotencyKey,
            // Replayed with the write, so the event it produces still names it
            // (#800). An unattributed echo can be swallowed by a live mark.
            ...(item.writeId ? { "X-Write-Id": item.writeId } : {}),
            // The household and person the write was made for, and a marker
            // that this is a replay (#105). The server only logs a mismatch.
            ...expectedIdentityHeaders(item),
            [OFFLINE_REPLAY_HEADER]: "1"
          },
          credentials: "include",
          body: item.body ?? undefined
        });
      } catch {
        // Network error — still offline. Keep this entry and the rest.
        break;
      }
      if (response.ok || isPermanentRejection(response.status)) {
        // Applied, or permanently rejected (validation/permission): drop it so
        // the queue can't wedge on one bad entry.
        await deleteMutation(item.id);
        if (response.ok) sent += 1;
      } else {
        // Transient (5xx/401/408/429): retry the whole tail later.
        break;
      }
    }
  } catch {
    // IndexedDB unavailable; leave whatever is queued for the next attempt.
  } finally {
    flushing = false;
  }
  const remaining = await countQueuedMutations();
  emitPendingCount();
  return { sent, remaining };
}

// Manual "discard" for the pending-sync UI: drop every queued mutation.
export async function discardQueue(): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).clear();
    await txDone(tx);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
  emitPendingCount();
}

// When Background Sync fires while an app window is open, the SW delegates the
// flush to the window (a single replay path avoids the SW and a live tab
// double-sending). Listen for that message here.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === OFFLINE_FLUSH_MESSAGE) {
      void flushQueue();
    }
  });
}
